import lunr from 'lunr'

// lunr has no diacritic folding, so "sao paulo" would miss "São Paulo".
const foldDiacritics = (token) => token.update((s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, ''))
lunr.Pipeline.registerFunction(foldDiacritics, 'foldDiacritics')

// Filters the past-results listing over the already-rendered DOM: the list stays server-rendered so
// the year links remain crawlable and the fragment cache keeps paying off.
const Results = {
  initialize() {
    const $events = $('#results .event')
    if ($events.length === 0) return

    this.$events = $events
    this.$empty = $('#results-empty')
    this.$input = $('#results-search')
    this.$year = $('#results-year')

    this._appliedYear = ''

    this._buildIndex()
    this._populateYears()
    this._bind()
  },

  // Championship names are proper nouns matched as substrings: the stemmer and the stop-word filter
  // would mangle them at index time. Same rationale as the academy search.
  _buildIndex() {
    const documents = []
    this.$events.each((position, el) => {
      documents.push({ id: String(position), name: String($(el).data('championship-name') || '') })
    })

    this.index = lunr(function () {
      this.pipeline.remove(lunr.stemmer)
      this.pipeline.remove(lunr.stopWordFilter)
      this.searchPipeline.remove(lunr.stemmer)
      this.searchPipeline.remove(lunr.stopWordFilter)
      // Before the trimmer, which is ASCII-only and would eat the trailing "ń" of "Poznań".
      this.pipeline.before(lunr.trimmer, foldDiacritics)

      this.ref('id')
      this.field('name')
      documents.forEach((doc) => this.add(doc), this)
    })
  },

  // Years come from the rendered chips so the options stay in sync with the cached fragment.
  _populateYears() {
    const years = new Set()
    $('#results .year').each((_, el) => {
      const year = String($(el).data('year') || '')
      if (year !== '') years.add(year)
    })

    const sorted = Array.from(years).sort((a, b) => Number(b) - Number(a))
    sorted.forEach((year) => this.$year.append($('<option>').attr('value', year).text(year)))
  },

  _bind() {
    this.$input.on('input', () => this.apply())
    this.$year.on('change', () => this.apply())

    const $clear = $('#results-search-clear')
    $clear.on('click', () => {
      if (this.$input.val().trim() !== '') {
        this.$input.val('')
        this.apply()
      }
      this.$input.trigger('focus')
    })
  },

  apply() {
    const query = this.$input.val().trim()
    const words = this._tokenize(query)
    const year = this.$year.val() || ''

    this._toggleClearIcon(query !== '')

    const byName = words.length > 0 ? this._search(words) : null
    const yearChanged = year !== this._appliedYear
    let visible = 0

    this.$events.each((position, el) => {
      const $event = $(el)
      const nameMatches = byName === null || byName.has(String(position))
      const yearMatches = year === '' || $event.find(`.year[data-year="${year}"]`).length > 0

      const show = nameMatches && yearMatches
      $event.toggle(show)
      if (show) visible += 1

      // With a year picked the row collapses to that chip: reaching one edition on a phone. The
      // chips only depend on the year, so typing must not walk all two thousand of them again.
      if (yearChanged) {
        $event.find('.year').each((_, yearEl) => {
          const $year = $(yearEl)
          $year.toggle(year === '' || String($year.data('year')) === year)
        })
      }
    })

    this._appliedYear = year
    this.$empty.prop('hidden', visible > 0)
  },

  // The typed text goes through the same tokenizer and trimmer the index used: splitting on spaces
  // alone left "Jiu-Jitsu" or "D.C." as a single REQUIRED term that no indexed token can match.
  _tokenize(text) {
    return lunr
      .tokenizer(text)
      .map((token) => lunr.trimmer(foldDiacritics(token)).toString())
      .filter(Boolean)
  },

  // lunr's default search is OR, so every typed word is required explicitly, each as a substring.
  _search(words) {
    const results = this.index.query((query) => {
      words.forEach((word) => {
        query.term(word, {
          wildcard: lunr.Query.wildcard.LEADING | lunr.Query.wildcard.TRAILING,
          presence: lunr.Query.presence.REQUIRED,
        })
      })
    })

    return new Set(results.map((result) => result.ref))
  },

  // The field icon doubles as a clear button.
  _toggleClearIcon(hasText) {
    const $clear = $('#results-search-clear')
    const $icon = $clear.find('i')

    $icon.toggleClass('fa-search', !hasText).toggleClass('fa-times', hasText)
    $clear.attr('aria-label', $clear.data(hasText ? 'clearLabel' : 'searchLabel'))
  },
}

export default Results
